import React, { useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';
import {
  type CommunityGroupComment,
  useCommunityGroupAccess,
  useCommunityGroupPost,
  useCommunityGroupPostComments,
  useCommunityGroupPostEngagement,
  useCommunityGroupPostSeenList,
  useCommunityGroupPostRealtime,
  useRecordCommunityGroupPostsSeen,
  useCreateCommunityGroupComment,
  useSetCommunityGroupPostCommentPinned,
  useSetCommunityGroupPostPinned,
  useToggleCommunityGroupPostLike,
} from '../hooks/useCommunityGroupPosts';
import { SeenByDialog } from '../components/SeenByDialog';
import PostRichContent from '../components/social/PostRichContent';

const Page = styled.div`
  min-height: 100%;
  background:
    radial-gradient(ellipse 120% 80% at 50% -20%, color-mix(in srgb, var(--primary) 28%, transparent) 0%, transparent 55%),
    radial-gradient(ellipse 60% 40% at 100% 40%, color-mix(in srgb, var(--primary) 8%, transparent) 0%, transparent 50%),
    var(--background);
  padding: clamp(1rem, 2.5vw, 1.75rem);
`;

const Shell = styled.div`
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FrostCard = styled.section`
  border: 1px solid color-mix(in srgb, var(--outline) 85%, transparent);
  border-radius: 22px;
  background: linear-gradient(
    145deg,
    color-mix(in srgb, var(--surface) 94%, transparent) 0%,
    color-mix(in srgb, var(--surface) 88%, transparent) 100%
  );
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow:
    0 1px 0 color-mix(in srgb, var(--onSurface) 6%, transparent),
    0 12px 40px rgba(0, 0, 0, 0.12),
    0 2px 8px rgba(0, 0, 0, 0.06);
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  width: fit-content;
  text-decoration: none;
  color: var(--primary);
  font-size: 0.86rem;
  font-weight: 700;
`;

const PostCard = styled(FrostCard)`
  padding: clamp(1rem, 2vw, 1.2rem);
`;

const Title = styled.h1`
  margin: 0;
  color: var(--onSurface);
  font-family: 'Poppins', system-ui, sans-serif;
  font-size: clamp(1.25rem, 2.4vw, 1.6rem);
  font-weight: 700;
  letter-spacing: -0.02em;
`;

const Meta = styled.p`
  margin: 0.35rem 0 0;
  color: var(--onSurfaceVariant);
  font-size: 0.82rem;
`;

const BadgeRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 0.85rem;
`;

const Badge = styled.span<{ $tone?: 'highlight' | 'neutral' | 'pinned' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.38rem 0.68rem;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 700;
  color: ${({ $tone }) =>
    $tone === 'highlight' || $tone === 'pinned' ? 'var(--primary)' : 'var(--onSurfaceVariant)'};
  background: ${({ $tone }) =>
    $tone === 'highlight' || $tone === 'pinned'
      ? 'color-mix(in srgb, var(--primary) 10%, transparent)'
      : 'color-mix(in srgb, var(--surfaceVariant, var(--surface)) 68%, transparent)'};
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'highlight' || $tone === 'pinned'
        ? 'color-mix(in srgb, var(--primary) 22%, transparent)'
        : 'color-mix(in srgb, var(--outline) 75%, transparent)'};
`;

const CommentAuthorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-wrap: wrap;
`;

const StatRow = styled.div`
  margin-top: 1rem;
  padding-top: 0.9rem;
  border-top: 1px solid color-mix(in srgb, var(--outline) 70%, transparent);
  display: flex;
  align-items: center;
  gap: 0.7rem;
  flex-wrap: wrap;
`;

const Stat = styled.span`
  font-size: 0.78rem;
  color: var(--onSurfaceVariant);
`;

const ActionButton = styled.button<{ $active?: boolean }>`
  border: 1px solid
    ${({ $active }) =>
      $active
        ? 'color-mix(in srgb, var(--primary) 34%, transparent)'
        : 'color-mix(in srgb, var(--outline) 78%, transparent)'};
  background: ${({ $active }) =>
    $active
      ? 'color-mix(in srgb, var(--primary) 10%, var(--surface))'
      : 'color-mix(in srgb, var(--surface) 84%, transparent)'};
  color: ${({ $active }) => ($active ? 'var(--primary)' : 'var(--onSurfaceVariant)')};
  border-radius: 999px;
  padding: 0.45rem 0.78rem;
  font-size: 0.78rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
`;

const SectionCard = styled(FrostCard)`
  padding: clamp(1rem, 2vw, 1.2rem);
`;

const SectionTitle = styled.h2`
  margin: 0;
  color: var(--onSurface);
  font-family: 'Poppins', system-ui, sans-serif;
  font-size: 1rem;
  font-weight: 700;
`;

const SectionText = styled.p`
  margin: 0.45rem 0 0;
  color: var(--onSurfaceVariant);
  font-size: 0.82rem;
  line-height: 1.5;
`;

const ReplyBanner = styled.div`
  margin-top: 0.8rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 0.8rem;
  border-radius: 14px;
  background: color-mix(in srgb, var(--surfaceVariant, var(--surface)) 62%, transparent);
  border: 1px solid color-mix(in srgb, var(--outline) 75%, transparent);
`;

const ReplyBannerText = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  font-size: 0.78rem;
`;

const GhostButton = styled.button`
  border: 1px solid color-mix(in srgb, var(--outline) 82%, transparent);
  background: color-mix(in srgb, var(--surface) 72%, transparent);
  color: var(--onSurfaceVariant);
  border-radius: 999px;
  padding: 0.38rem 0.72rem;
  font-size: 0.76rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
`;

const CommentInput = styled.textarea`
  resize: vertical;
  min-height: 120px;
  width: 100%;
  box-sizing: border-box;
  margin-top: 0.85rem;
  border-radius: 18px;
  border: 1px solid color-mix(in srgb, var(--outline) 88%, transparent);
  background: color-mix(in srgb, var(--surfaceVariant, var(--surface)) 55%, var(--surface));
  color: var(--onSurface);
  padding: 0.95rem 1rem;
  font-size: 0.92rem;
  line-height: 1.5;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 22%, transparent);
  }
`;

const ActionRow = styled.div`
  margin-top: 0.8rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.65rem;
`;

const PrimaryButton = styled.button`
  border: none;
  background: linear-gradient(
    165deg,
    color-mix(in srgb, var(--primary) 92%, white) 0%,
    var(--primary) 45%,
    var(--primaryDark, var(--primary)) 100%
  );
  color: var(--onPrimary);
  border-radius: 12px;
  padding: 0.55rem 1rem;
  font-size: 0.82rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
`;

const CommentList = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const CommentCard = styled.div<{ $depth: number }>`
  margin-left: ${({ $depth }) => `${Math.min($depth, 3) * 1.2}rem`};
  padding: 0.9rem 1rem;
  border-radius: 18px;
  border: 1px solid color-mix(in srgb, var(--outline) 74%, transparent);
  background: ${({ $depth }) =>
    $depth === 0
      ? 'color-mix(in srgb, var(--surface) 88%, transparent)'
      : 'color-mix(in srgb, var(--surfaceVariant, var(--surface)) 56%, transparent)'};
`;

const CommentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  align-items: center;
`;

const CommentAuthor = styled.strong`
  color: var(--onSurface);
  font-size: 0.8rem;
`;

const CommentContent = styled.div`
  margin: 0.45rem 0 0;
`;

const EmptyText = styled.p`
  margin: 0.8rem 0 0;
  color: var(--onSurfaceVariant);
  font-size: 0.82rem;
`;

type CommentNode = CommunityGroupComment & { replies: CommentNode[] };

const formatDate = (value?: string | null) => {
  if (!value) return 'Recently';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Recently';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const buildCommentTree = (comments: CommunityGroupComment[]): CommentNode[] => {
  const nodes = new Map<string, CommentNode>();
  comments.forEach((comment) => {
    nodes.set(comment.id, { ...comment, replies: [] });
  });

  const roots: CommentNode[] = [];
  nodes.forEach((node) => {
    const parentId = node.reply_to_id ?? null;
    if (parentId && nodes.has(parentId)) {
      nodes.get(parentId)?.replies.push(node);
    } else {
      roots.push(node);
    }
  });

  return roots;
};

export const CommunityGroupPostPage: React.FC = () => {
  const { groupId, postId } = useParams<{ groupId: string; postId: string }>();
  const { user } = useAuth();
  const { data: profile } = useCurrentUserProfile(user?.id);
  const [commentValue, setCommentValue] = useState('');
  const [replyTarget, setReplyTarget] = useState<CommunityGroupComment | null>(null);
  const [isSeenDialogOpen, setIsSeenDialogOpen] = useState(false);
  const commentInputRef = useRef<HTMLTextAreaElement | null>(null);
  const roles = profile?.roles ?? user?.user_metadata?.roles;
  const backHref = `/groups/${groupId}?tab=feed`;

  useCommunityGroupPostRealtime({ enabled: Boolean(postId), postId });

  const access = useCommunityGroupAccess(groupId, user?.id, roles);
  const { data: post, isLoading: postLoading, error: postError } = useCommunityGroupPost(postId);
  const { data: comments, isLoading: commentsLoading, error: commentsError } = useCommunityGroupPostComments(postId);
  const { data: engagement } = useCommunityGroupPostEngagement(user?.id, postId);
  const { data: seenList, isLoading: seenListLoading } = useCommunityGroupPostSeenList(
    postId,
    access.canViewSeenStatus && isSeenDialogOpen
  );
  const recordSeen = useRecordCommunityGroupPostsSeen();
  const toggleLike = useToggleCommunityGroupPostLike(user?.id);
  const createComment = useCreateCommunityGroupComment(user, profile?.full_name);
  const setPostPinned = useSetCommunityGroupPostPinned(groupId);
  const setCommentPinned = useSetCommunityGroupPostCommentPinned();

  const commentTree = useMemo(() => buildCommentTree(comments ?? []), [comments]);
  const liked = Boolean(engagement?.liked);
  const canPinContent = Boolean(access.canPinContent);

  React.useEffect(() => {
    if (!post?.id || !user?.id || !access.membership) return;
    recordSeen.mutate([post.id]);
  }, [access.membership, post?.id, recordSeen, user?.id]);

  const handleReply = (comment: CommunityGroupComment) => {
    setReplyTarget(comment);
    commentInputRef.current?.focus();
  };

  const handleSubmitComment = async () => {
    if (!postId) return;

    await createComment.mutateAsync({
      postId,
      content: commentValue,
      replyToId: replyTarget?.id,
    });
    setCommentValue('');
    setReplyTarget(null);
  };

  const handleTogglePostPinned = async () => {
    if (!post?.id) return;
    await setPostPinned.mutateAsync({ postId: post.id, pinned: !Boolean(post.is_pinned) });
  };

  const handleToggleCommentPinned = async (comment: CommunityGroupComment) => {
    await setCommentPinned.mutateAsync({
      commentId: comment.id,
      pinned: !Boolean(comment.is_pinned),
    });
  };

  const renderThread = (nodes: CommentNode[], depth = 0): React.ReactNode =>
    nodes.map((node) => (
      <React.Fragment key={node.id}>
        <CommentCard $depth={depth}>
          <CommentHeader>
            <CommentAuthorRow>
              <CommentAuthor>{node.author_name || 'Community Member'}</CommentAuthor>
              {node.is_pinned ? <Badge $tone="pinned">Pinned</Badge> : null}
            </CommentAuthorRow>
            <Meta>{formatDate(node.created_at)}</Meta>
          </CommentHeader>
          <CommentContent>
            <PostRichContent text={node.content} compact />
          </CommentContent>
          <ActionRow style={{ marginTop: '0.65rem', justifyContent: 'flex-start' }}>
            {depth < 1 ? (
              <GhostButton type="button" onClick={() => handleReply(node)}>
                Reply
              </GhostButton>
            ) : null}
            {canPinContent ? (
              <GhostButton
                type="button"
                onClick={() => void handleToggleCommentPinned(node)}
                disabled={setCommentPinned.isPending}
              >
                {node.is_pinned ? 'Unpin' : 'Pin'}
              </GhostButton>
            ) : null}
          </ActionRow>
        </CommentCard>
        {node.replies.length ? renderThread(node.replies, depth + 1) : null}
      </React.Fragment>
    ));

  return (
    <Page>
      <Shell>
        <BackLink to={backHref}>
          {`← Back to ${access.group?.name || 'group feed'}`}
        </BackLink>

        <PostCard>
          {postLoading || access.isLoading ? (
            <SectionText>Loading this group conversation...</SectionText>
          ) : postError || access.error ? (
            <SectionText>
              {postError instanceof Error
                ? postError.message
                : access.error instanceof Error
                ? access.error.message
                : 'Unable to load this post.'}
            </SectionText>
          ) : !access.canViewGroupContent ? (
            <SectionText>
              {access.group?.is_private
                ? 'Join this private group to view the full post conversation.'
                : 'This post could not be viewed right now.'}
            </SectionText>
          ) : post ? (
            <>
              <Title>{access.group?.name || 'Group conversation'}</Title>
              <Meta>
                {post.author_name || 'Community Member'} • {formatDate(post.created_at)}
              </Meta>
              <BadgeRow>
                <Badge $tone="highlight">{post.type || 'post'}</Badge>
                <Badge>{post.status || 'published'}</Badge>
                {post.is_pinned ? <Badge $tone="pinned">Pinned</Badge> : null}
              </BadgeRow>
              <div style={{ marginTop: '0.9rem' }}>
                <PostRichContent
                  text={post.content || 'No post body provided.'}
                  images={post.images ?? []}
                  videoUrl={post.video_url ?? undefined}
                  imageAlt={`${post.author_name || 'Group'} post`}
                />
              </div>
              <StatRow>
                <Stat>{post.likesCount} likes</Stat>
                <Stat>{post.commentsCount} comments</Stat>
                {access.canViewSeenStatus && post.seenCount !== null ? (
                  <ActionButton type="button" onClick={() => setIsSeenDialogOpen(true)}>
                    Seen by {post.seenCount ?? 0}
                  </ActionButton>
                ) : null}
                <ActionButton
                  type="button"
                  $active={liked}
                  onClick={() => postId && toggleLike.mutateAsync(postId)}
                  disabled={toggleLike.isPending}
                >
                  {liked ? 'Liked' : 'Like'}
                </ActionButton>
                {canPinContent ? (
                  <ActionButton
                    type="button"
                    onClick={() => void handleTogglePostPinned()}
                    disabled={setPostPinned.isPending}
                  >
                    {post.is_pinned ? 'Unpin' : 'Pin'}
                  </ActionButton>
                ) : null}
              </StatRow>
            </>
          ) : (
            <SectionText>This post could not be found.</SectionText>
          )}
        </PostCard>

        <SectionCard>
          <SectionTitle>Join the thread</SectionTitle>
          <SectionText>
            Reply directly to the post or choose a comment below to keep the conversation grouped.
          </SectionText>
          {!access.canCreateGroupContent ? (
            <SectionText>Join this group if you want to add comments.</SectionText>
          ) : (
            <>
              {replyTarget ? (
                <ReplyBanner>
                  <ReplyBannerText>
                    Replying to {replyTarget.author_name || 'Community Member'}: {replyTarget.content.slice(0, 60)}
                  </ReplyBannerText>
                  <GhostButton type="button" onClick={() => setReplyTarget(null)}>
                    Cancel reply
                  </GhostButton>
                </ReplyBanner>
              ) : null}
              <CommentInput
                ref={commentInputRef}
                placeholder={replyTarget ? 'Write a nested reply' : 'Write a comment to keep the conversation going'}
                value={commentValue}
                onChange={(event) => setCommentValue(event.target.value)}
              />
              <ActionRow>
                <GhostButton type="button" onClick={() => setReplyTarget(null)}>
                  Clear
                </GhostButton>
                <PrimaryButton
                  type="button"
                  onClick={handleSubmitComment}
                  disabled={createComment.isPending || !commentValue.trim()}
                >
                  {createComment.isPending ? 'Posting...' : replyTarget ? 'Post Reply' : 'Post Comment'}
                </PrimaryButton>
              </ActionRow>
            </>
          )}
        </SectionCard>

        <SectionCard>
          <SectionTitle>Replies</SectionTitle>
          <SectionText>Replies stay grouped under the parent comment, matching the mobile flow.</SectionText>
          {commentsLoading ? <EmptyText>Loading comments...</EmptyText> : null}
          {commentsError ? (
            <EmptyText>{commentsError instanceof Error ? commentsError.message : 'Unable to load comments.'}</EmptyText>
          ) : null}
          {!commentsLoading && !commentsError && commentTree.length === 0 ? (
            <EmptyText>Be the first to reply to this post.</EmptyText>
          ) : null}
          <CommentList>{renderThread(commentTree)}</CommentList>
        </SectionCard>

        <SeenByDialog
          open={isSeenDialogOpen}
          title={post ? `Seen by ${post.seenCount ?? 0}` : 'Seen by'}
          items={seenList ?? []}
          loading={seenListLoading}
          onClose={() => setIsSeenDialogOpen(false)}
        />
      </Shell>
    </Page>
  );
};

export default CommunityGroupPostPage;